// Perspective camera policy, kept independent of rendering and input platforms.
// Heading 0 faces +Z. Orbit yaw is measured from +X toward +Z.
const TAU = Math.PI * 2;
const DEFAULT_PITCH = .65;
const MIN_PITCH = .25;
const MAX_PITCH = 1.18;
const finite = (value, fallback) => Number.isFinite(value) ? value : fallback;
const coordinate = (value, fallback) => Number.isFinite(value) && Math.abs(value) <= 1e6 ? value : fallback;
const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
const wrap = value => ((value + Math.PI) % TAU + TAU) % TAU - Math.PI;
const copyPoint = point => ({ x: point.x, y: point.y, z: point.z });
const ease = (current, desired, dt, rate) => current + (desired - current) * -Math.expm1(-rate * dt);

function aspectOf(viewport) {
  if (Number.isFinite(viewport?.width) && viewport.width > 0 && Number.isFinite(viewport?.height) && viewport.height > 0)
    return clamp(viewport.width / viewport.height, .25, 4);
  return Number.isFinite(viewport?.aspect) && viewport.aspect > 0 ? clamp(viewport.aspect, .25, 4) : 16 / 9;
}

function followPoint(current, desired, previous, dt, rate, continuousMovement) {
  if (dt === 0) return copyPoint(current);
  const scaledTime = rate * dt, alpha = -Math.expm1(-scaledTime);
  // Integrate a linearly moving focus exactly during a continuous walk or sail.
  // This removes the frame-size lag difference of a simple end-point lerp.
  const beta = continuousMovement ? (scaledTime < 1e-4 ? scaledTime / 2 - scaledTime * scaledTime / 6 : 1 - alpha / scaledTime) : 0;
  const result = {};
  for (const axis of ['x','y','z']) result[axis] = continuousMovement
    ? current[axis] + (previous[axis] - current[axis]) * alpha + (desired[axis] - previous[axis]) * beta
    : ease(current[axis], desired[axis], dt, rate);
  return result;
}

export function createExplorationCamera() {
  let selectedMode = 'follow', orbitYaw = -.72, orbitPitch = DEFAULT_PITCH, zoomMultiplier = 1;
  let initialized = false, focus = { x: 0, y: 0, z: 0 }, actor = { x: 0, y: 0, z: 0 };
  let previousDesired = copyPoint(focus), previousMoving = false, previousProfile = '';
  let currentDistance = 14, currentPitch = DEFAULT_PITCH, lastHeading = 0;

  return {
    get mode() { return selectedMode; },
    get yaw() { return orbitYaw; },
    get zoom() { return zoomMultiplier; },
    get pitch() { return orbitPitch; },
    // Drag distances are CSS pixels. Rotation is immediate; focus/distance ease.
    rotate(dx, dy) {
      orbitYaw = wrap(orbitYaw - finite(dx, 0) * .006);
      orbitPitch = clamp(orbitPitch + finite(dy, 0) * .0045, MIN_PITCH, MAX_PITCH);
    },
    setZoom(value) {
      zoomMultiplier = clamp(finite(value, zoomMultiplier), .7, 1.45);
      return zoomMultiplier;
    },
    toggleMode() {
      selectedMode = selectedMode === 'follow' ? 'overview' : 'follow';
      return selectedMode;
    },
    recenter(heading) {
      lastHeading = wrap(finite(heading, lastHeading));
      orbitYaw = wrap(-lastHeading - Math.PI / 2);
      return orbitYaw;
    },
    step(dt, context = {}) {
      const { position, heading, moving = false, sailing = false, inside = false, overview, viewport, collisionDistance } = context ?? {};
      const elapsed = Math.max(0, finite(dt, 0));
      if (position && typeof position === 'object') {
        actor = { x: coordinate(position.x, actor.x), y: coordinate(position.y, actor.y), z: coordinate(position.z, actor.z) };
      }
      lastHeading = wrap(finite(heading, lastHeading));
      const aspect = aspectOf(viewport), portrait = aspect < 1, fov = portrait ? 58 : 52;
      // Indoor framing temporarily takes priority without losing the player's
      // chosen overview mode when they step back outside.
      const effectiveMode = inside ? 'follow' : selectedMode;
      const profile = inside ? 'inside' : effectiveMode === 'overview' ? 'overview' : sailing ? 'sailing' : 'walking';
      let desired, baseDistance, basePitch;
      if (profile === 'overview') {
        const radius = clamp(finite(overview?.radius, 14), 3, 48);
        desired = { x: coordinate(overview?.x, actor.x), y: coordinate(overview?.y, 0), z: coordinate(overview?.z, actor.z) };
        // A circle on the island plane must also fit the narrower horizontal
        // field of view in portrait, rather than merely increasing the FOV.
        const framingDistance = radius * 1.16 / (Math.tan(fov * Math.PI / 360) * Math.min(1, aspect));
        baseDistance = Math.max(46 * radius / 14, framingDistance);
        basePitch = 1.05;
      } else {
        const lead = moving && !inside ? (sailing ? 1.1 : .65) : 0;
        desired = { x: actor.x + Math.sin(lastHeading) * lead, y: actor.y + (sailing ? .55 : .75), z: actor.z + Math.cos(lastHeading) * lead };
        baseDistance = inside ? 7 : sailing ? (portrait ? 22 : 19) : (portrait ? 17 : 14);
        basePitch = inside ? .9 : DEFAULT_PITCH;
      }
      const desiredDistance = clamp(baseDistance * zoomMultiplier, .2, 190);
      const desiredPitch = clamp(basePitch + orbitPitch - DEFAULT_PITCH, MIN_PITCH, MAX_PITCH);
      const continuousMovement = !!moving && previousMoving && profile === previousProfile && profile !== 'overview';
      const sceneJump = initialized && profile !== 'overview' && previousProfile !== 'overview'
        && Math.hypot(desired.x - previousDesired.x, desired.z - previousDesired.z) > 24;
      if (!initialized || sceneJump) {
        focus = copyPoint(desired); currentDistance = desiredDistance; currentPitch = desiredPitch; initialized = true;
      } else {
        focus = followPoint(focus, desired, previousDesired, elapsed, profile === 'overview' ? 4 : 7, continuousMovement);
        currentDistance = ease(currentDistance, desiredDistance, elapsed, 4.5);
        currentPitch = ease(currentPitch, desiredPitch, elapsed, 6);
      }
      // A wall takes effect immediately. Once it clears, the ordinary distance
      // easing restores the view smoothly instead of snapping outward.
      if (Number.isFinite(collisionDistance) && collisionDistance >= 0)
        currentDistance = Math.min(currentDistance, Math.max(.2, collisionDistance));
      previousDesired = copyPoint(desired); previousMoving = !!moving; previousProfile = profile;
      const horizontal = currentDistance * Math.cos(currentPitch);
      return {
        position: { x: focus.x + Math.cos(orbitYaw) * horizontal, y: focus.y + Math.sin(currentPitch) * currentDistance, z: focus.z + Math.sin(orbitYaw) * horizontal },
        target: copyPoint(focus), fov, near: Math.min(.1, currentDistance / 10), far: 260,
        distance: currentDistance, mode: effectiveMode, yaw: orbitYaw, pitch: currentPitch,
      };
    },
  };
}
